import './applySearchVinPage.scss';

import Box from '@mui/material/Box';
import { ThemeProvider } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import isEmpty from 'lodash/isEmpty';
import toString from 'lodash/toString';
import toUpper from 'lodash/toUpper';
import { ReactNode, useEffect } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Button } from '../../../components/Button/Button';
import { Header } from '../../../components/Header/Header';
import { NavigationPane } from '../../../components/NavigationPane/NavigationPane';
import useApiStore from '../../../store/apiStore/useApiStore';
import useApplyStore from '../../../store/applyStore/useApplyStore';
import useThemeStore from '../../../store/themeStore/useThemeStore';
import useWorkflowStore from '../../../store/workflowStore/useWorkflowStore';
import { locator } from '../../../testing/e2e-locator/locator';

export function Component(): ReactNode {
  const { createTheme } = useThemeStore();
  const theme = createTheme();
  const navigate = useNavigate();

  const { isLoading } = useApiStore();
  const { t } = useTranslation();
  const { next, previous } = useWorkflowStore();

  const { vin, fetchVehicleBy, fetchVehicleFailedMessage } = useApplyStore();

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm({
    mode: 'onBlur',
    delayError: 200,
    defaultValues: {
      vinValue: vin,
    },
  });

  const vinValidation = {
    pattern: {
      message: t('validation.vin.invalidCharacters'),
      value: /^[A-HJ-NPR-Z0-9]+$/i,
    },
    required: t('validation.vin.required'),
  };

  useEffect((): void => {
    // clear any fail message state upon rendering
    useApplyStore.setState({ fetchVehicleFailedMessage: null });
  }, []);

  const previousStep = (): void => {
    navigate(previous());
  };

  const formSubmit = async (values: FieldValues): Promise<void> => {
    const { vinValue } = values;

    if (vinValue) {
      await fetchVehicleBy({ vin: toUpper(vinValue) });

      if (useApplyStore.getState().fetchVehicleSuccess) {
        navigate(next());
      }
    }
  };

  return (
    <div className="apply-search-vin-page">
      <ThemeProvider theme={theme}>
        <Box sx={theme.pageLayout}>
          <Header
            progress={useWorkflowStore.getState().computed.progress}
            showDrawerToggle
            onBackClick={previousStep}
          />

          <main>
            <form
              data-testid={locator.pages.applySearchVin.form.root}
              noValidate
              onSubmit={handleSubmit(formSubmit)}
            >
              <div className="form-content">
                <h1 data-testid={locator.pages.applySearchVin.form.h1}>
                  {t('ApplySearchVinPage.title')}
                </h1>
                <h2 data-testid={locator.pages.applySearchVin.form.h2}>
                  {t('ApplySearchVinPage.subtitle')}
                </h2>

                <div style={{ marginBottom: '3rem' }}>
                  <TextField
                    {...register('vinValue', vinValidation)}
                    error={
                      !isEmpty(errors.vinValue) ||
                      !isEmpty(fetchVehicleFailedMessage)
                    }
                    label={t('ApplySearchVinPage.vin')}
                    slotProps={{
                      htmlInput: {
                        'data-testid':
                          locator.pages.applySearchVin.form.vinInput,
                      },
                    }}
                    variant="outlined"
                    fullWidth
                  />

                  {(!isEmpty(errors.vinValue) ||
                    !isEmpty(fetchVehicleFailedMessage)) && (
                    <span
                      className="field-error"
                      data-testid={
                        locator.pages.applySearchVin.form.vinInputError
                      }
                    >
                      {toString(
                        errors.vinValue?.message || fetchVehicleFailedMessage,
                      )}
                    </span>
                  )}
                </div>

                <div style={{ marginBottom: '3rem' }}>
                  <Button
                    data-testid={locator.pages.applySearchVin.form.submitButton}
                    disabled={!isValid}
                    loader={isLoading}
                    loaderPosition="end"
                    fullWidth
                    submit
                  >
                    {isLoading
                      ? t('ApplySearchVinPage.searching')
                      : t('ApplySearchVinPage.continue')}
                  </Button>
                </div>
              </div>
              <footer data-testid={locator.pages.applySearchVin.footer.root}>
                {t('copyright', { year: new Date().getFullYear() })}
              </footer>
            </form>
          </main>
        </Box>

        <NavigationPane withDrawer withUserNameHeader />
      </ThemeProvider>
    </div>
  );
}

Component.displayName = 'ApplySearchVinPage';
